import { Component, OnInit } from '@angular/core';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';
import { NanoService } from 'src/app/shared/nano/nano.service';
import { AuthService } from 'src/app/shared/server-services/auth.service';
import { NanoSlotRecord } from 'src/app/shared/server-services/query-records/nano-records';
import { DialogService } from 'src/app/shared/services/dialog.service';
import { RouterHandler } from 'src/app/shared/services/router-handler.service';
import { TitleService } from 'src/app/shared/services/title.service';

import { MatDialog } from '@angular/material/dialog';
import { base64 } from 'rfc4648';
import { Subject } from 'rxjs';
import {
  NanoManagerDialogComponent,
  NanoManagerDialogData,
} from 'src/app/shared/dialogs/nano-manager-dialog/nano-manager-dialog.component';
import { NANO_VERSION_FOR_BACKUP } from 'src/app/shared/drive-version';
import { RoomService } from 'src/app/shared/server-services/room.service';

export type PrivyInfoResult = { name: string; version: string };

@Component({
  selector: 'app-privy',
  templateUrl: './privy.component.html',
  styleUrl: './privy.component.scss',
})
export class PrivyComponent implements OnInit {
  public roomId;
  public nanoSlots: NanoSlotRecord[];
  private apiParams: {
    sk: Uint8Array;
    p1: Uint8Array;
    p4?: Uint8Array;
  };
  public requiredVersion = NANO_VERSION_FOR_BACKUP;

  constructor(
    private routerHandler: RouterHandler,
    private authService: AuthService,
    private dialogService: DialogService,
    private titleService: TitleService,
    private nanoService: NanoService,
    private roomService: RoomService,
    protected dialog: MatDialog
  ) {
    this.titleService.setCurrentTabTitle(marker('Private Backup Setup'));
  }

  ngOnInit(): void {
    let { sk, p1, p4 } = this.routerHandler.getRoute().fragment;
    if (sk && p1) {
      console.log('ok');
      try {
        this.apiParams = {
          sk: base64.parse(sk),
          p1: base64.parse(p1),
        };
        if (p4) {
          this.apiParams['p4'] = base64.parse(p4);
        }
      } catch (e) {
        this.dialogService.openAlertDialog(
          marker('Error'),
          marker('Wrong URL. Can not parse tokens.')
        );
        this.routerHandler.navigate(['/']);
        return;
      }
    } else {
      this.dialogService.openAlertDialog(
        marker('Error'),
        marker('Wrong URL. Some tokens are missing.')
      );
      this.routerHandler.navigate(['/']);
      return;
    }

    if (this.authService.isAnonym()) {
      this.dialogService.openAlertDialog(
        marker('Error'),
        marker('Please log in before accessing this page.')
      );
      this.routerHandler.navigate(['/login'], {
        fragment: this.routerHandler.fragmentToRaw({
          sk,
          p1,
          nav: 'privy/setup',
        }),
      });

      return;
    }
  }

  public backupSetupInProgress = false;
  public privyInfo: PrivyInfoResult;
  public privyGetInfoInProgress = false;

  public startBackup() {
    this.backupSetupInProgress = true;
    this.roomService
      .createPrivySetup(this.apiParams.sk, this.apiParams.p1, this.apiParams.p4)
      .then(async (roomId) => {
        this.roomId = roomId;
        this.backupSetupInProgress = false;

        this.privyGetInfoInProgress = true;
        return this.getPrivyInfo(roomId)
          .then((info: PrivyInfoResult) => {
            this.privyGetInfoInProgress = false;
            console.log('info', info);
            this.privyInfo = info;
          })
          .catch((e) => {
            this.dialogService.openAlertDialog(
              marker('Error'),
              marker(
                'Privy synchronization completed, but an error occurred while requesting room information. You can start a manual setup, but quick setup is not available in this state.'
              ),
              e
            );
            this.privyGetInfoInProgress = false;
          });
      })
      .catch(async (e) => {
        this.dialogService.openAlertDialog(
          marker('Error'),
          marker('Error happened during the privy room creation'),
          e
        );
        this.backupSetupInProgress = false;
      });
  }

  private getPrivyInfo(roomId) {
    return new Promise<PrivyInfoResult>((resolve, reject) => {
      try {
        var retry = 10;
        var retryWaitTime = 3000;
        var currPrivyRoom;

        const repeatSubj$: Subject<void> = new Subject<void>();
        repeatSubj$.subscribe(async () => {
          console.log('start... ' + retry);
          if (!currPrivyRoom) {
            let privyRooms = await this.roomService.getPrivyRooms();
            console.log('rooms', privyRooms, roomId);
            currPrivyRoom = privyRooms.find((r) => r.roomId == roomId);

            // we should wait for it
            if (!currPrivyRoom) {
              if (retry-- > 0) {
                await new Promise((resolve) => {
                  setTimeout(resolve, retryWaitTime);
                });
                repeatSubj$.next();
                return;
              } else {
                repeatSubj$.complete();
                reject('Could not find the privy room');
                return;
              }
            }
          }

          this.nanoService.privyGetInfo(
            currPrivyRoom,
            (resp: PrivyInfoResult) => {
              repeatSubj$.complete();
              resolve(resp);
            },
            async (err) => {
              // waiting for privy connection
              if ((err?.message == 'Invalid argument!' || err?.error == '4102') && retry-- > 0) {
                await new Promise((resolve) => {
                  setTimeout(resolve, retryWaitTime);
                });
                repeatSubj$.next();
              } else {
                console.error('get info error', err);
                repeatSubj$.complete();
                reject(err);
              }
            }
          );
        });

        repeatSubj$.next();
      } catch (e) {
        reject(e);
      }
    });
  }

  public deletePrivy(roomId) {
    this.roomService
      .deletePrivyRoom(roomId)
      .then((res) => {
        console.log('res', res);
      })
      .catch((err) => {
        console.log('err', err);
      });
  }

  public openNanoManagerDialog(): void {
    this.dialog
      .open(NanoManagerDialogComponent, { autoFocus: false })
      .afterClosed()
      .subscribe(() => {
        this.routerHandler.navigate(['']);
      });
  }

  public quickSetupStarted = false;
  public quickSetup() {
    this.quickSetupStarted = true;
    let param: NanoManagerDialogData = {
      privy: {
        id: this.roomId,
        name: this.privyInfo.name,
      },
    };
    this.dialog
      .open(NanoManagerDialogComponent, { autoFocus: false, data: param })
      .afterClosed()
      .subscribe((resp) => {
        if (resp.privy) {
          // success
          if (resp.attachedRoomId) {
            this.routerHandler.navigate(['room', resp.attachedRoomId, 'drive']);
          }
        } else {
          // error
          this.dialogService.openAlertDialog(
            marker('Error'),
            marker('Error happened during the process'),
            resp.error
          );
        }
      });
  }
}
